import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Paper,
  Box,
  Typography,
  Tooltip,
  TextField,
  Button,
  Autocomplete,
  MenuItem,
  Collapse,
  IconButton,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Science,
  Cancel,
  CalendarToday,
  AccessTime,
  LocalShipping,
  CheckCircleOutline,
  DeliveryDining,
  Sell,
  ErrorOutline,
  Close,
  ExpandMore,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getScraperSSEToken } from '../api/scraperService';
import { useScrapeStream } from '../hooks/useScrapeStream';
import ProgressStream from './common/ProgressStream';
import DisabledReason from './common/DisabledReason';
import useFokusNachLaden from './common/hooks/useFokusNachLaden';
import CompetitorModal from './CompetitorModal';
import LastScrapeSummarySkeleton from '../Skeletons/LastScrapeSummarySkeleton';
import CartAwarePriceBadge from './common/CartAwarePriceBadge';
import ScraperSettingsFields from './ScraperSettingsFields';
import { guessVatRate, coerceVatInput } from '../utils/vat';
import MarketSelector from './MarketSelector';
import PageContainer from './common/PageContainer';
import ViewHeader from './ViewHeader';
import Panel from './common/Panel';
import { PANEL_SX } from '../theme/tokens';
import { formatDate, formatTime } from '../utils/format';

const API = process.env.REACT_APP_API_BASE_URL || '';




const StatusDot = ({ active, reason, size = 10 }) => (
  <Tooltip
    title={active ? 'Target is active' : `Target is inactive${reason ? ` — ${reason}` : ''}`}
    arrow
  >
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: active ? 'success.main' : 'grey.500',
        boxShadow: active
          ? '0 0 8px 2px rgba(46, 204, 113, 0.55)'
          : '0 0 6px 1px rgba(158,158,158,0.45)',
      }}
    />
  </Tooltip>
);

// --- NEW: Reusable Compact Stat for the Summary & Test Results ---
const CompactStat = ({ icon, title, value, valueColor }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ mr: 1.5, color: 'text.secondary' }}>{icon}</Box>
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', fontSize: 9 }}>
                {title}
            </Typography>
            <Typography variant="body2" component="p" color={valueColor || 'text.primary'} sx={{ fontWeight: 500 }}>
                {value}
            </Typography>
        </Box>
    </Box>
);

// --- Modern Last Scrape Summary Component (fixed) ---
const ModernLastScrapeSummary = ({ data, active, inactiveReason, currencyCode = 'EUR' }) => {
  // App-canonical de-DE currency (matches ProgressStream money() + MarketPricingOverview)
  // so every scraper surface reads a price identically — e.g. "279,00 €", "279,00 kr."
  const fmtPrice = (v) => {
    if (v === null || v === undefined) return 'N/A';
    const n = typeof v === 'number' ? v : Number(v);
    if (Number.isNaN(n)) return 'N/A';
    try {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: currencyCode || 'EUR' }).format(n);
    } catch (_e) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
    }
  };
  if (!data || !data.date) {
    return (
      <Panel>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Last Scrape Summary
          </Typography>
          <Chip
            size="small"
            label={active ? 'Active' : 'Inactive'}
            color={active ? 'success' : 'default'}
            variant={active ? 'filled' : 'outlined'}
          />
        </Stack>
        <Alert severity="info" variant="outlined">
          No scrape data available for this target yet.
        </Alert>
      </Panel>
    );
  }

  const { date, price, isSale, shipping, availability, deliveryTime, deliveryBand } = data;
  const scrapeDate = new Date(date);
  const deliveryColor = deliveryBand === 'delayed'
    ? 'error.main'
    : deliveryBand === 'extended'
      ? 'warning.main'
      : 'text.primary';
  const deliveryLabel = deliveryBand === 'delayed'
    ? 'Delayed (>10 days)'
    : deliveryBand === 'extended'
      ? 'Extended (7–10 days)'
      : 'Normal';

  return (
    <Panel sx={{ position: 'relative' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Last Scrape Summary
        </Typography>
        <Chip
          size="small"
          label={active ? 'Active' : 'Inactive'}
          color={active ? 'success' : 'default'}
          variant={active ? 'filled' : 'outlined'}
        />
      </Stack>
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <StatusDot active={active} reason={inactiveReason} size={12} />
      </Box>
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, md: 5 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.grey[900]
                  : theme.palette.action.hover,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
              Last Price
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h3" component="p" sx={{ fontWeight: 600 }}>
                {fmtPrice(price)}
              </Typography>
              {isSale && <Chip label="SALE" color="error" size="small" icon={<Sell />} variant="filled" />}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5 }}>
              {availability ? (
                <CheckCircleOutline color="success" sx={{ fontSize: '1rem' }} />
              ) : (
                <ErrorOutline color="warning" sx={{ fontSize: '1rem' }} />
              )}
              <Typography
                variant="body2"
                color={availability ? 'success.main' : 'warning.main'}
                sx={{ fontWeight: 500 }}
              >
                {availability ? 'In Stock' : 'Out of Stock'}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CompactStat
                icon={<CalendarToday fontSize="small" />}
                title="Scrape Date"
                value={formatDate(scrapeDate)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CompactStat
                icon={<AccessTime fontSize="small" />}
                title="Scrape Time"
                value={formatTime(scrapeDate)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CompactStat
                icon={<LocalShipping fontSize="small" />}
                title="Shipping"
                value={fmtPrice(shipping)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CompactStat
                icon={<DeliveryDining fontSize="small" sx={{ color: deliveryColor }} />}
                title="Delivery Time"
                value={deliveryTime ? `${deliveryTime} • ${deliveryLabel}` : 'Not Found'}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Panel>
  );
};

// --- Redesigned Test Output Section — uses ProgressStream for live phases + raw log ---
const TestOutputSection = ({ show, onClose, scrapeState, result, onCancelTest, currencyCode = 'EUR' }) => {
  // App-canonical de-DE currency (matches ProgressStream money() + MarketPricingOverview)
  // so every scraper surface reads a price identically — e.g. "279,00 €", "279,00 kr."
  const fmtPrice = (v) => {
    if (v === null || v === undefined) return 'N/A';
    const n = typeof v === 'number' ? v : Number(v);
    if (Number.isNaN(n)) return 'N/A';
    try {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: currencyCode || 'EUR' }).format(n);
    } catch (_e) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
    }
  };
  const loading = scrapeState.status === 'running';

  return (
    <Collapse in={show} unmountOnExit>
      <Panel>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ ml: 1, fontWeight: 'bold' }}>
            Test Output
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close"><Close /></IconButton>
        </Box>

        <Box sx={{ p: 1 }}>
          <ProgressStream {...scrapeState} />
        </Box>

        {/* Detailed final result panel — shown after test completes */}
        {result && (
          <Box sx={{ px: 2, pb: 2 }}>
            {result.status === 'error' ? (
              <Alert severity="error" variant="standard" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                <AlertTitle sx={{ fontWeight: 'bold' }}>Test Failed: {result.message || 'An error occurred'}</AlertTitle>
                {result.hint && <Alert severity="info" sx={{ mt: 1, mb: 2 }}><strong>Hint:</strong> {result.hint}</Alert>}
                <Accordion sx={{ mt: 2, boxShadow: 'none', border: '1px solid rgba(0,0,0,0.1)', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="body2">Show Error Details</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all', mt: 0.5, p: 1.5, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                      {result.details || 'No details available.'}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Alert>
            ) : (
              <Box>
                <Chip icon={<CheckCircle />} label="Test Succeeded" color="success" sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 3 }}><CompactStat icon={result.availability ? <CheckCircleOutline color="success" /> : <ErrorOutline color="warning" />} title="Availability" value={result.availability ? 'In Stock' : 'Out of Stock'} valueColor={result.availability ? 'success.main' : 'warning.main'} /></Grid>
                  <Grid size={{ xs: 12, sm: 3 }}><CompactStat icon={<Sell />} title="Price" value={fmtPrice(result.price)} /></Grid>
                  {(result.isSale || result.is_sale) && (
                    <Box sx={{ mt: 1 }}>
                      <Chip label="SALE" color="error" size="small" />
                    </Box>
                  )}
                  {/* Phase 3.3 — cart-aware sticker callout when ShopifyCartFetcher or
                      HtmlFetcher observed a discount. */}
                  {result.sticker_price != null && Number(result.sticker_price) > Number(result.price) && (
                    <Grid size={12}>
                      <Box sx={{ mt: 0.5, pl: 1 }}>
                        <CartAwarePriceBadge
                          price={result.price}
                          stickerPrice={result.sticker_price}
                          available={result.availability !== false}
                          size="small"
                        />
                      </Box>
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: 3 }}><CompactStat icon={<LocalShipping />} title="Shipping" value={fmtPrice(result.shipping_cost)} /></Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    {(() => {
                      const band = result.delivery_band;
                      const color = band === 'delayed' ? 'error.main' : band === 'extended' ? 'warning.main' : 'text.primary';
                      const label = band === 'delayed'
                        ? 'Delayed (>10 days)'
                        : band === 'extended'
                          ? 'Extended (7–10 days)'
                          : 'Normal';
                      return (
                        <CompactStat
                          icon={<DeliveryDining sx={{ color }} />}
                          title="Delivery Time"
                          value={result.delivery_time ? `${result.delivery_time} • ${label}` : 'N/A'}
                        />
                      );
                    })()}
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}

        {loading && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Button size="small" startIcon={<Cancel />} onClick={onCancelTest}>Cancel test</Button>
          </Box>
        )}
      </Panel>
    </Collapse>
  );
};


export default function ScraperConfigForm() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // one ref to manage the SSE EventSource (used by "Test" run)
  const navigate = useNavigate();
  const location = useLocation();
  const { configId } = useParams();
  const isEdit = Boolean(configId);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const prefillSku = searchParams.get('sku')?.trim() || null;
  const prefillProductLabel = searchParams.get('product_label')?.trim() || null;
  const prefillCompetitorId = searchParams.get('competitor')?.trim() || null;
  const prefillCompetitorLabel = searchParams.get('competitor_label')?.trim() || null;
  const prefillMarketId = searchParams.get('market_id')?.trim() || null;
  const prefillMarketCode = searchParams.get('market_code')?.trim() || null;
  const prefillUrl = searchParams.get('url')?.trim() || null;

  // --- Form State ---
  const [initialState, setInitialState] = useState(null);
  const [formState, setFormState] = useState({
    product: null,
    market: null,
    competitor: null,
    url: '',
  });
  const [isDirty, setIsDirty] = useState(false);
  const { product, competitor, market, url: targetUrl } = formState;

  // --- Data for dropdowns ---
  const [products, setProducts] = useState([]);
  // const [markets, setMarkets] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [allMarkets, setAllMarkets] = useState([]);
  
  // --- Last Scrape Data State ---
  const [lastScrapeData, setLastScrapeData] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  // 🔴 W-46 (2026-09-28): SIEBEN Abrufe scheiterten STILL — `.catch((e) => { console.error(e); setLoadError(true); })`
  // schrieb in die Konsole, und das Formular sah danach aus, als gaebe es keine
  // Optionen. Wer eine Konfiguration anlegt, sieht leere Dropdowns und weiss
  // nicht, ob das an ihm liegt oder am System. Das ist die Silent-Zero-Klasse,
  // diesmal in einem Formular statt in einer Kennzahl.
  const [loadError, setLoadError] = useState(false);

  // --- Target Status State (for status dot/tooltip) ---
  const [isTargetActive, setIsTargetActive] = useState(true);
  const [inactiveReason, setInactiveReason] = useState(null);

  // --- Competitor Settings State ---
  const [showSelectors, setShowSelectors] = useState(false);
  const [editDefaultsMode, setEditDefaultsMode] = useState(false);
  const [competitorDefaults, setCompetitorDefaults] = useState({
    technique: 'html',
    applyVat: false,
    vatRate: '',
    availabilityStrategies: [],
    priceSelector: '',
    priceAttr: '',
    salePriceSelector: '',
    salePriceAttr: '',
    saleDetectionMethod: 'dedicated_sale_price',
    saleIndicatorSelector: '',
    saleIndicatorText: '',
    shippingRules: [],
    stockMethod: 'disabled_attribute',
    stockSelector: '',
    stockText: '',
    deliveryTimeSelector: '',
    xpathPriceSelector: '',
    xpathPriceAttr: '',
    xpathSalePriceSelector: '',
    xpathSalePriceAttr: '',
    xpathAvailabilitySelector: '',
    xpathAvailabilityText: [],
  });
    const [shippingErrors, setShippingErrors] = useState({});
  
  // Other UI states
  const [openCompWizard, setOpenCompWizard] = useState(false);
  const [testResult, setTestResult] = useState(null);
  // --- Persist Modal State ---
  const [modalOpen, setModalOpen] = useState(false);
  const [countdown, setCountdown] = useState(10);

  // --- useScrapeStream hook (shared between test + persist runs) ---
  const scrape = useScrapeStream();
  // Depend on the STABLE stop fn (useCallback([]) in useScrapeStream), NOT the
  // `scrape` object — that object is a fresh literal every render, so [scrape]
  // fires this cleanup on EVERY render and closes the EventSource the same tick
  // start() opens it (aborting the request before it's even sent). Mount/unmount only.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- exhaustive-deps wants [scrape], but scrape is a fresh object each render → that reintroduces the every-render cleanup bug; scrape.stop is stable (useCallback([]))
  useEffect(() => () => scrape.stop(), [scrape.stop]);
  // Tracks which run mode is active ('test' | 'persist' | null) so we can
  // derive testLoading / persisting / success from a single hook state.
  const runModeRef = useRef(null);

  // Derived booleans from hook state + run mode
  const testLoading = scrape.state.status === 'running' && runModeRef.current === 'test';
  const persisting  = scrape.state.status === 'running' && runModeRef.current === 'persist';
  const success     = scrape.state.status === 'success'  && runModeRef.current === 'persist';
  // 🔴 K-F1 (2026-10-10): der Test-Knopf sperrt sich waehrend des Laufs und
  // verliert dabei den Fokus — der Haken holt ihn danach zurueck.
  const testKnopf = useRef(null);
  useFokusNachLaden(testKnopf, testLoading);
  const normalizeArrayValue = (value) => {
    if (Array.isArray(value)) return value;
    if (!value) return [];
    return String(value)
      .split(/[\n,]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  // Hard delete open dialog similar to list (you can reuse same dialog component)
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);
  const [hdOpen, setHdOpen] = useState(false);
  const [hdData, setHdData] = useState(null);
  const [hdLoading, setHdLoading] = useState(false);
  const [hdChoice, setHdChoice] = useState('delete_all');
  const [hdTarget, setHdTarget] = useState('');
  
  // --- NEW: State for showing/hiding the test output section ---
  const [showTestOutput, setShowTestOutput] = useState(false);

  const validateShippingRules = (rules) => {
    const errors = {};
    rules.forEach((rule, index) => {
      // Check if threshold is empty
      if (rule.threshold === '' || rule.threshold === null) {
        errors[index] = { ...errors[index], threshold: true };
      }
      // Check if cost is empty
      if (rule.cost === '' || rule.cost === null) {
        errors[index] = { ...errors[index], cost: true };
      }
    });
    setShippingErrors(errors);
    // Return true if there are no errors
    return Object.keys(errors).length === 0;
  };

  const handleFormChange = (field, value) => {
    setFormState(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };
  
  const handleDefaultsChange = (field, value) => {
    setCompetitorDefaults(prev => ({...prev, [field]: value}));
    setIsDirty(true);
  };


  useEffect(() => {
    axios.get(`${API}/api/products?status=active`)
      .then(({ data }) =>
        setProducts(data.map(p => ({
          label: `${p.product_name} — ${p.variant_name}`,
          sku:   p.sku
        })))
      )
      .catch((e) => { console.error(e); setLoadError(true); });

    axios.get(`${API}/api/scrapers/competitors`)
      .then(({ data }) =>
        setCompetitors([
          ...data.map(c => ({ id: c.id, label: c.name, name: c.name })),
          { id: 'add', label: 'Add competitor…' }
        ])
      )
      .catch((e) => { console.error(e); setLoadError(true); });

      // --- For shipping rules UI (not the market field of the form) ---
    axios.get(`${API}/api/scrapers/markets`)
      .then(({ data }) => setAllMarkets(data))
      .catch((e) => { console.error(e); setLoadError(true); });
  }, []);

  useEffect(() => {
    if (isEdit) return;
    let touched = false;
    const next = { ...formState };

    if (prefillSku && !product) {
      const productMatch = products.find((p) => p.sku === prefillSku);
      next.product = productMatch || { label: prefillProductLabel || prefillSku, sku: prefillSku };
      touched = true;
    }

    if (prefillCompetitorId && !competitor) {
      const competitorMatch = competitors.find(
        (c) => String(c.id) === String(prefillCompetitorId) && c.id !== 'add'
      );
      if (competitorMatch) {
        next.competitor = {
          id: competitorMatch.id,
          label: competitorMatch.label || competitorMatch.name || competitorMatch.id,
        };
        touched = true;
      } else if (prefillCompetitorLabel) {
        next.competitor = { id: prefillCompetitorId, label: prefillCompetitorLabel };
        touched = true;
      }
    }

    if (!market && (prefillMarketId || prefillMarketCode)) {
      const marketMatch =
        allMarkets.find((m) => String(m.id) === String(prefillMarketId)) ||
        (prefillMarketCode
          ? allMarkets.find(
              (m) => String(m.code || '').toLowerCase() === prefillMarketCode.toLowerCase()
            )
          : null);
      if (marketMatch) {
        next.market = marketMatch;
        touched = true;
      }
    }

    if (prefillUrl && !targetUrl) {
      next.url = prefillUrl;
      touched = true;
    }

    if (touched) {
      setFormState(next);
      setInitialState(next);
    }
  }, [
    allMarkets,
    competitor,
    competitors,
    formState,
    isEdit,
    market,
    prefillCompetitorId,
    prefillCompetitorLabel,
    prefillMarketCode,
    prefillMarketId,
    prefillProductLabel,
    prefillSku,
    prefillUrl,
    product,
    products,
    targetUrl,
  ]);

  useEffect(() => {
    if (!isEdit) return;

    setLoadingSummary(true);
    axios.get(`${API}/api/scrapers/config/${configId}`)
      .then(({ data: cfg }) => {
        const initial = {
          product:    { label: `${cfg.product_name} — ${cfg.variant_name}`, sku: cfg.sku },
          competitor: { id: cfg.competitor_id, label: cfg.competitor_name },
          url:        cfg.product_url,
          market:     null, // set properly below after we load the options
        };
        setFormState(initial);
        setInitialState(JSON.parse(JSON.stringify(initial)));

        // Resolve the initial market object for the selector
        if (cfg.market_id) {
          axios
            .get(`${API}/api/scrapers/markets`, { params: { sku: cfg.sku } })
            .then(({ data }) => {
              // data is [{id, code, name, currency_code}, ...]
              const found = (data || []).find(m => String(m.id) === String(cfg.market_id));
              setFormState(prev => ({ ...prev, market: found || null }));
              setInitialState(prev => prev ? { ...prev, market: found || null } : prev);
            })
            .catch((e) => { console.error(e); setLoadError(true); });
        }
        
        setLastScrapeData({
            date: cfg.last_scraped_at,
            price: cfg.last_price,
            shipping: cfg.last_shipping,
            availability: cfg.last_availability,
            deliveryTime: cfg.last_delivery_time,
            deliveryBand: cfg.last_delivery_band,
            isSale: cfg.last_is_sale
        });

        // NEW: set activity status for the status dot
        setIsTargetActive(Boolean(cfg.active));
        setInactiveReason(cfg.inactive_reason || null);
        
        axios.get(`${API}/api/scrapers/competitors/${cfg.competitor_id}/defaults`)
          .then(({ data: defaults }) => {
            const dc = defaults?.default_config || {};
            setCompetitorDefaults({
              technique:            dc.defaultTechnique || 'html',
              applyVat:             Boolean(dc.applyVat),
              vatRate:              dc.vatRate ?? '',
              availabilityStrategies: Array.isArray(dc.availabilityStrategies) ? dc.availabilityStrategies : [],
              priceSelector:        dc.priceSelector || '',
              priceAttr:            dc.priceAttr || '',
              salePriceSelector:    dc.salePriceSelector || '',
              salePriceAttr:        dc.salePriceAttr || '',
              saleDetectionMethod:  dc.saleDetectionMethod || 'dedicated_sale_price',
              saleIndicatorSelector:dc.saleIndicatorSelector || '',
              saleIndicatorText:    dc.saleIndicatorText || '',
              stockMethod:          dc.stockMethod || 'disabled_attribute',
              stockSelector:        dc.stockSelector || '',
              stockText:            dc.stockText || '',
              deliveryTimeSelector: dc.deliveryTimeSelector || '',
              xpathPriceSelector:   dc.xpathPriceSelector || '',
              xpathPriceAttr:       dc.xpathPriceAttr || '',
              xpathSalePriceSelector: dc.xpathSalePriceSelector || '',
              xpathSalePriceAttr:     dc.xpathSalePriceAttr || '',
              xpathAvailabilitySelector: dc.xpathAvailabilitySelector || '',
              xpathAvailabilityText: normalizeArrayValue(dc.xpathAvailabilityText),
              shippingRules:        defaults.shippingRules || [],
            });
            setEditDefaultsMode(false);
          });
      })
      .catch((e) => { console.error(e); setLoadError(true); })
      .finally(() => setLoadingSummary(false));
  }, [configId, isEdit]);

  useEffect(() => {
        if (!formState.competitor || formState.competitor.id === 'add' || !formState.competitor.id) {
            setCompetitorDefaults({
                technique: 'html',
                applyVat: false,
                vatRate: '',
                priceSelector: '', priceAttr: '',
                salePriceSelector: '', salePriceAttr: '',
                saleDetectionMethod: 'dedicated_sale_price',
                saleIndicatorSelector: '', saleIndicatorText: '',
                shippingRules: [], stockMethod: 'disabled_attribute',
                stockSelector: '', stockText: '', deliveryTimeSelector: '',
                xpathPriceSelector: '', xpathPriceAttr: '',
                xpathSalePriceSelector: '', xpathSalePriceAttr: '',
                xpathAvailabilitySelector: '', xpathAvailabilityText: [],
            });
            setEditDefaultsMode(false);
            return;
        }
        
        axios.get(`${API}/api/scrapers/competitors/${formState.competitor.id}/defaults`)
          .then(({ data: defaults }) => {
            const dc = defaults?.default_config || {};
            setCompetitorDefaults({
              technique:            dc.defaultTechnique || 'html',
              applyVat:             Boolean(dc.applyVat),
              vatRate:              dc.vatRate ?? '',
              availabilityStrategies: Array.isArray(dc.availabilityStrategies) ? dc.availabilityStrategies : [],
              priceSelector:        dc.priceSelector || '',
              priceAttr:            dc.priceAttr || '',
              salePriceSelector:    dc.salePriceSelector || '',
              salePriceAttr:        dc.salePriceAttr || '',
              saleDetectionMethod:  dc.saleDetectionMethod || 'dedicated_sale_price',
              saleIndicatorSelector:dc.saleIndicatorSelector || '',
              saleIndicatorText:    dc.saleIndicatorText || '',
              stockMethod:          dc.stockMethod || 'disabled_attribute',
              stockSelector:        dc.stockSelector || '',
              stockText:            dc.stockText || '',
              deliveryTimeSelector: dc.deliveryTimeSelector || '',
              xpathPriceSelector:   dc.xpathPriceSelector || '',
              xpathPriceAttr:       dc.xpathPriceAttr || '',
              xpathSalePriceSelector: dc.xpathSalePriceSelector || '',
              xpathSalePriceAttr:     dc.xpathSalePriceAttr || '',
              xpathAvailabilitySelector: dc.xpathAvailabilitySelector || '',
              xpathAvailabilityText: normalizeArrayValue(dc.xpathAvailabilityText),
              shippingRules:        defaults.shippingRules || [],
            });
            setEditDefaultsMode(false);
          })
            .catch((e) => { console.error(e); setLoadError(true); });
    }, [formState.competitor]);

  useEffect(() => {
    if (!formState.market) return;
    setCompetitorDefaults(prev => {
      if (prev.vatRate !== '' && prev.vatRate !== null && prev.vatRate !== undefined) {
        return prev;
      }
      const inferred = guessVatRate(formState.market);
      if (inferred == null) return prev;
      return { ...prev, vatRate: inferred };
    });
  }, [formState.market]);

  // Sync testResult from hook state when a test run finishes
  useEffect(() => {
    if (!scrape.state.result) return;
    const data = scrape.state.result;
    setTestResult({
      ...data,
      technique: data.technique,
      status: 'success',
      isSale: (data.is_sale ?? data.isSale ?? false),
    });
  }, [scrape.state.result]);

  // Sync target-active status from result payload (preserved from old result listener)
  useEffect(() => {
    if (scrape.state.result == null) return;
    if (typeof scrape.state.result.target_inactive !== 'undefined') {
      setIsTargetActive(!scrape.state.result.target_inactive);
      setInactiveReason(scrape.state.result.inactive_reason || null);
    }
  }, [scrape.state.result]);

  const handleCancel = () => {
    if (initialState) {
      setFormState(initialState);
    } else {
      setFormState({ product: null, market: null, competitor: null, url: '' });
    }
    setIsDirty(false);
  };

  // inside ScraperConfigForm.jsx
  const onSave = async (e) => {
    e.preventDefault();

    // Guard: all these are required for backend to resolve properly
    if (!formState.product?.sku || !formState.market?.id || !formState.competitor?.id || !formState.url) {
      alert('Please select a Product, Market, Competitor and enter a URL.');
      return;
    }

    // Optional: run validation so shippingErrors reflects current state
    if (!validateShippingRules(competitorDefaults.shippingRules || [])) {
      alert('Please complete shipping rules (threshold & cost).');
      return;
    }

    // IMPORTANT: send a resolvable market. Prefer id; include code/name as fallbacks.
    const payload = {
      sku:           formState.product?.sku || null,
      product_url:   formState.url || '',
      competitor_id: formState.competitor?.id || null,
      // resolver-friendly triplet
      market_id:     formState.market?.id   ?? null,
      market_code:   formState.market?.code ?? null,
      market:        formState.market?.name ?? null,
    };

    try {
      // 1) persist competitor defaults only if we have a competitor AND the user saw/edited them
      if (formState.competitor?.id && showSelectors) {
            const vatInput = coerceVatInput(competitorDefaults.vatRate);
            const defaultsPayload = {
              default_config: {
                defaultTechnique:     competitorDefaults.technique || 'html',
                applyVat:             Boolean(competitorDefaults.applyVat),
                vatRate:              vatInput,
                availabilityStrategies: Array.isArray(competitorDefaults.availabilityStrategies) ? competitorDefaults.availabilityStrategies : [],
                priceSelector:        competitorDefaults.priceSelector || '',
                priceAttr:            competitorDefaults.priceAttr || '',
                salePriceSelector:    competitorDefaults.salePriceSelector || '',
                salePriceAttr:        competitorDefaults.salePriceAttr || '',
                saleDetectionMethod:  competitorDefaults.saleDetectionMethod || 'dedicated_sale_price',
                saleIndicatorSelector:competitorDefaults.saleIndicatorSelector || '',
                saleIndicatorText:    competitorDefaults.saleIndicatorText || '',
                stockMethod:          competitorDefaults.stockMethod || 'disabled_attribute',
                stockSelector:        competitorDefaults.stockSelector || '',
                stockText:            competitorDefaults.stockText || '',
                deliveryTimeSelector: competitorDefaults.deliveryTimeSelector || '',
                xpathPriceSelector:   competitorDefaults.xpathPriceSelector || '',
                xpathPriceAttr:       competitorDefaults.xpathPriceAttr || '',
                xpathSalePriceSelector: competitorDefaults.xpathSalePriceSelector || '',
                xpathSalePriceAttr:     competitorDefaults.xpathSalePriceAttr || '',
                xpathAvailabilitySelector: competitorDefaults.xpathAvailabilitySelector || '',
                xpathAvailabilityText: normalizeArrayValue(competitorDefaults.xpathAvailabilityText),
                // important: shippingRules can be top-level or nested; backend accepts both
                shippingRules:        competitorDefaults.shippingRules || [],
              },
          // also provide at top-level so the backend’s current code sees them immediately
          shippingRules: (competitorDefaults.shippingRules || []),
        };

        await axios.put(`${API}/api/scrapers/competitors/${formState.competitor.id}/defaults`, defaultsPayload);
      }

      // 2) save/update the target itself
      if (isEdit) {
        await axios.put(`${API}/api/scrapers/config/${configId}`, payload);
      } else {
        await axios.post(`${API}/api/scrapers/config`, payload);
      }

      setIsDirty(false);
    } catch (err) {
      console.error(err);
    }
  };

  
  const onTest = async () => {
    if (!formState.url) return;

    // Show the output section and reset result
    setShowTestOutput(true);
    setTestResult(null);
    runModeRef.current = 'test';

    // EventSource cannot send Authorization headers — fetch a short-lived
    // SSE token first (Bearer-authed) and append to URL.
    let sseToken;
    try {
      sseToken = await getScraperSSEToken();
    } catch (err) {
      setTestResult({ status: 'error', message: 'Connection to the scraper service failed.' });
      runModeRef.current = null;
      return;
    }

    const params = new URLSearchParams({
      product_url:   formState.url,
      target_id:     isEdit ? configId : '',
      competitor_id: formState.competitor?.id || '',
      market_id:     formState.market?.id   || '',
      market_code:   formState.market?.code || '',
      market:        formState.market?.name || '',
      persist:       'false',
      token:         sseToken,
    });

    scrape.start({ url: `${API}/api/scrapers/config/run?${params.toString()}` });
  };

  const handleCancelTest = () => {
    scrape.stop();
    runModeRef.current = null;
  };

  const handlePersist = async () => {
    setModalOpen(true);
    setLoadingSummary(true);
    runModeRef.current = 'persist';

    // SSE token first (EventSource cannot send Authorization headers).
    let sseToken;
    try {
      sseToken = await getScraperSSEToken();
    } catch (err) {
      setLoadingSummary(false);
      runModeRef.current = null;
      return;
    }

    const params = new URLSearchParams({
      product_url: formState.url,
      target_id: configId || '',
      competitor_id: formState.competitor?.id || '',
      market_id: formState.market?.id || '',
      persist: 'true',
      token: sseToken,
    });

    // onClose: post-stream axios.get refresh — preserved from the old close listener
    scrape.start({
      url: `${API}/api/scrapers/config/run?${params.toString()}`,
      onClose: () => {
        axios.get(`${API}/api/scrapers/config/${configId}`)
          .then(({ data: cfg }) => {
            setLastScrapeData({
              date:         cfg.last_scraped_at,
              price:        cfg.last_price,
              shipping:     cfg.last_shipping,
              availability: cfg.last_availability,
              deliveryTime: cfg.last_delivery_time,
              deliveryBand: cfg.last_delivery_band,
              isSale:       cfg.last_is_sale
            });
            setIsTargetActive(Boolean(cfg.active));
            setInactiveReason(cfg.inactive_reason || null);
          })
          .catch((e) => { console.error(e); setLoadError(true); })
          .finally(() => setLoadingSummary(false));
      },
    });
  };

  const handleCloseModal = () => {
    if (!persisting) setModalOpen(false);
  };

  useEffect(() => {
    // Trigger auto-close countdown when a persist run completes successfully
    if (scrape.state.status !== 'success' || runModeRef.current !== 'persist') return;
    setCountdown(10);
    const timer = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(timer);
          handleCloseModal();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleCloseModal is stable after mount
  }, [scrape.state.status]);

  // Toggle active
  const handleDeactivate = () => {
    setConfirmDeactivateOpen(true);
  };
  const execDeactivate = () => {
    axios.post(`${API}/api/scrapers/config/${configId}/deactivate`)
      .then(() => setIsTargetActive(false))
      .finally(() => setConfirmDeactivateOpen(false));
  };
  const handleActivate = () => {
    axios.post(`${API}/api/scrapers/config/${configId}/activate`)
      .then(() => setIsTargetActive(true));
  };

  // Hard delete: open preview, then execute chosen mode
  const openHardDelete = () => {
    setHdOpen(true);
    setHdLoading(true);
    axios.get(`${API}/api/scrapers/config/${configId}/hard_delete_preview`)
      .then(({data}) => {
        setHdData(data);
        setHdChoice('delete_all');
        setHdTarget('');
      })
      .finally(() => setHdLoading(false));
  };

  const execHardDelete = () => {
    const body = hdChoice === 'migrate'
      ? { mode: 'migrate', new_target_id: hdTarget }
      : { mode: 'delete_all' };

    setHdLoading(true);
    axios.post(`${API}/api/scrapers/config/${configId}/hard_delete`, body)
      .then(() => {
        // after hard delete, go back to list
        navigate('/scrapers');
      })
      .finally(() => setHdLoading(false));
  };
 
  
  const canPersist = formState.competitor && formState.url;

  const productBreadcrumb = formState.product?.label
    ? {
        label: formState.product.label,
        href: formState.product?.sku ? `/products/${formState.product.sku}` : undefined,
      }
    : null;
  const fallbackParent = productBreadcrumb
    ? productBreadcrumb
    : formState.competitor?.label
    ? {
        label: formState.competitor.label,
        href: formState.competitor?.id ? `/competitors/${formState.competitor.id}` : undefined,
      }
    : { label: 'Scraper Targets', href: '/scrapers' };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    fallbackParent,
    { label: 'Scrape Target details' },
  ];

  const headerActions = [
    ...(isEdit || canPersist
      ? [
          {
            key: 'persist',
            label: 'Persist scraper results',
            onClick: handlePersist,
            disabled: !canPersist,
          },
        ]
      : []),
  ];

  const overflowActions = [];
  if (isEdit) {
    overflowActions.push({
      label: isTargetActive ? 'Deactivate target' : 'Activate target',
      onClick: isTargetActive ? handleDeactivate : handleActivate,
    });
    overflowActions.push({
      label: 'Hard delete',
      onClick: openHardDelete,
    });
  }

  const statusChip = isEdit ? (
    <Chip
      size="small"
      label={isTargetActive ? 'Active' : 'Inactive'}
      color={isTargetActive ? 'success' : 'default'}
      variant={isTargetActive ? 'filled' : 'outlined'}
    />
  ) : null;

  return (
    <PageContainer>
      {/* 🔴 W-46 (2026-09-28): sichtbarer Hinweis, wenn eine der Auswahllisten
          nicht geladen werden konnte. Vorher schrieb der Fehler nur in die
          Konsole, und das Formular sah aus, als gaebe es keine Optionen — der
          Nutzer konnte nicht unterscheiden, ob das an ihm liegt oder am System. */}
      {loadError && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          <AlertTitle>Einige Listen konnten nicht geladen werden</AlertTitle>
          Produkte, Wettbewerber oder Maerkte fehlen moeglicherweise in den
          Auswahlfeldern. Lade die Seite neu, bevor du speicherst — sonst
          speicherst du eine Konfiguration, die leer aussieht, weil sie es ist.
        </Alert>
      )}
      <ViewHeader
        backLink={{ label: 'Back to scraper targets', href: '/scrapers' }}
        title="Scrape Target details"
        breadcrumbs={breadcrumbs}
        actions={headerActions}
        overflow={overflowActions}
        // 🔴 B5 (UI-Durchgang 2, 2026-10-07): die Statuspille sitzt hinter dem
        // Titel, nicht im Aktionsbereich. Dort stand sie zwischen den Knöpfen
        // und sah wie einer aus; als Anzeige gehört sie zum Titel.
        titleAdornment={statusChip}
      />
      <Stack spacing={3} sx={{ pb: 6 }}>
        {isEdit &&
          (loadingSummary ? (
            <LastScrapeSummarySkeleton sx={PANEL_SX} />
          ) : (
            <ModernLastScrapeSummary
              data={lastScrapeData}
              active={isTargetActive}
              inactiveReason={inactiveReason}
              currencyCode={formState.market?.currency_code || 'EUR'}
            />
          ))}
      
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {isEdit ? 'Edit Scraper Target' : 'New Scraper Target'}
        </Typography>
        <Panel>
          <form onSubmit={onSave}>
            <fieldset disabled={testLoading} style={{ border: 'none', padding: 0, margin: 0 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', rowGap: 2.5, mt: 2 }}>
                <Autocomplete
                  options={products}
                  value={formState.product}
                  onChange={(_, v) => handleFormChange('product', v)}
                  getOptionLabel={(o) => o.label || ''}
                  isOptionEqualToValue={(o, v) => o.sku === v?.sku}
                  renderInput={(params) => <TextField {...params} label="Product" required />}
                />
                <MarketSelector
                  sku={formState.product?.sku}
                  value={formState.market}
                  onChange={(marketObj) => handleFormChange('market', marketObj)}
                  disabled={!formState.product}
                />
                <Autocomplete
                  options={competitors}
                  value={formState.competitor}
                  onChange={(_, v) =>
                    v?.id === 'add' ? setOpenCompWizard(true) : handleFormChange('competitor', v)
                  }
                  getOptionLabel={(o) => o.label || ''}
                  renderInput={(params) => <TextField {...params} label="Competitor" required />}
                />
                <TextField
                  label="Product URL"
                  fullWidth
                  required
                  value={formState.url}
                  onChange={(e) => handleFormChange('url', e.target.value)}
                />
              </Box>

              <Box sx={{ mt: 3 }}>
                <Button fullWidth onClick={() => setShowSelectors((s) => !s)}>
                  {showSelectors ? 'Hide competitor default settings' : 'Show competitor default settings'}
                </Button>
                <Collapse in={showSelectors}>
                  <ScraperSettingsFields
                    technique={competitorDefaults.technique}
                    onTechniqueChange={(val) => handleDefaultsChange('technique', val)}
                    config={competitorDefaults}
                    onChange={(field, value) => handleDefaultsChange(field, value)}
                    shippingRules={competitorDefaults.shippingRules}
                    shippingErrors={shippingErrors}
                    onShippingRulesChange={(rules) => handleDefaultsChange('shippingRules', rules)}
                    editMode={editDefaultsMode}
                    showShippingRules
                    showDeliveryTimeSelector
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                    <DisabledReason
                      reason={(!formState.competitor?.id || formState.competitor.id === 'add')
                        ? 'Save the competitor first — defaults can only be edited once it exists.'
                        : null}
                    >
                      <Button
                        size="small"
                        onClick={() => setEditDefaultsMode((prev) => !prev)}
                        disabled={!formState.competitor?.id || formState.competitor.id === 'add'}
                      >
                        {editDefaultsMode ? 'View mode' : 'Edit settings'}
                      </Button>
                    </DisabledReason>
                  </Box>
                </Collapse>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                <DisabledReason
                  reason={testLoading
                    ? null
                    : !isDirty ? 'Nothing has been changed yet.' : null}
                >
                  <Button onClick={handleCancel} disabled={!isDirty || testLoading}>
                    Cancel
                  </Button>
                </DisabledReason>
                <DisabledReason
                  reason={testLoading
                    ? null
                    : !isDirty
                      ? 'Nothing has been changed yet.'
                      : loadError
                        ? 'Reload the configuration first — the selector lists did not load.'
                        : Object.keys(shippingErrors).length > 0
                          ? 'Fix the shipping-rule errors first.'
                          : null}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    // `loadError` blocks the save. The banner above already TELLS
                    // the user to reload first; without this it only asked. Saving
                    // a configuration whose selector lists failed to load persists
                    // the empty form as if it were the user's intent (W-46 left
                    // this half done: the notice was added, the guard was not).
                    disabled={!isDirty || testLoading || loadError || Object.keys(shippingErrors).length > 0}
                  >
                    Save
                  </Button>
                </DisabledReason>
                <DisabledReason
                  reason={testLoading
                    ? null
                    : !formState.url ? 'Enter a URL first.' : null}
                >
                  {/* 🔴 K-F1 (2026-10-10): der Lauf zeigt sich am Knopf selbst —
                      Ring an der Stelle des Symbols statt Wortwechsel.
                      `loadingPosition="start"` haelt die Beschriftung lesbar
                      (die Vorgabe `center` setzt `color: transparent`); MUI
                      blendet dabei das `Science`-Symbol aus und haelt seinen
                      Platz, der Knopf springt also nicht. */}
                  <Button
                    ref={testKnopf}
                    variant="outlined"
                    startIcon={<Science />}
                    onClick={onTest}
                    disabled={!formState.url || testLoading}
                    loading={testLoading}
                    loadingPosition="start"
                    aria-busy={testLoading || undefined}
                  >
                    Test configuration
                  </Button>
                </DisabledReason>
              </Box>
            </fieldset>
          </form>
        </Panel>

        <TestOutputSection
          show={showTestOutput}
          onClose={() => setShowTestOutput(false)}
          scrapeState={scrape.state}
          result={testResult}
          onCancelTest={handleCancelTest}
          currencyCode={formState.market?.currency_code || 'EUR'}
        />
      </Stack>

      <Dialog open={confirmDeactivateOpen} onClose={() => setConfirmDeactivateOpen(false)}>
        <DialogTitle>Confirm Deactivation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will mark the target as <b>inactive</b>. It will be excluded from scheduled runs
            and recommendations, but all history stays intact. You can re-activate it later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeactivateOpen(false)}>Cancel</Button>
          <Button onClick={execDeactivate} autoFocus color="error">
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={hdOpen} onClose={() => setHdOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Hard delete target</DialogTitle>

        <DialogContent dividers>
          {hdLoading && <DialogContentText>Loading…</DialogContentText>}

          {!hdLoading && hdData && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>SKU:</b> {hdData.target.sku} · <b>Market:</b> {hdData.target.market_id || '—'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <b>Competitor ID:</b> {hdData.target.competitor_id || '—'}
              </Typography>

              <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:2, mb: 2 }}>
                <Paper variant="outlined" sx={{ p:1.5 }}>
                  <Typography variant="caption" color="text.secondary">Total rows</Typography>
                  <Typography variant="h6">{hdData.impact.total_rows}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p:1.5 }}>
                  <Typography variant="caption" color="text.secondary">Date range</Typography>
                  <Typography variant="body2">
                    {(hdData.impact.first_day || '—')} → {(hdData.impact.last_day || '—')}
                  </Typography>
                </Paper>
              </Box>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="hd-action-label">Action</InputLabel>
                <Select
                  labelId="hd-action-label"
                  value={hdChoice}
                  label="Action"
                  onChange={(e)=>setHdChoice(e.target.value)}
                >
                  <MenuItem value="delete_all">Delete rows and target</MenuItem>
                  <MenuItem value="migrate" disabled={!hdData.candidates.length}>
                    Migrate rows to another target
                  </MenuItem>
                </Select>
              </FormControl>

              {hdChoice === 'migrate' && (
                <FormControl fullWidth size="small">
                  <InputLabel id="hd-target-label">Destination target</InputLabel>
                  <Select
                    labelId="hd-target-label"
                    label="Destination target"
                    value={hdTarget}
                    onChange={(e)=>setHdTarget(e.target.value)}
                  >
                    {hdData.candidates.map(c => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.id.slice(0,8)}… · {c.market_id || '—'} · {c.active ? 'active' : 'inactive'}
                      </MenuItem>
                    ))}
                  </Select>
                  {!hdData.candidates.length && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display:'block' }}>
                      No compatible target found (same SKU + competitor + market). Create one first if you want to migrate.
                    </Typography>
                  )}
                </FormControl>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setHdOpen(false)}>Cancel</Button>
          <DisabledReason
            reason={hdLoading
              ? null
              : (hdChoice === 'migrate' && !hdTarget)
                ? 'Select a target competitor to migrate to.'
                : null}
          >
            <Button
              variant="contained"
              color="error"
              onClick={execHardDelete}
              disabled={hdLoading || (hdChoice === 'migrate' && !hdTarget)}
            >
              {hdChoice === 'migrate' ? 'Migrate & delete' : 'Delete'}
            </Button>
          </DisabledReason>
        </DialogActions>
      </Dialog>

      
      <Dialog open={modalOpen} onClose={(e, reason) => { if (reason === 'backdropClick' || persisting) return; handleCloseModal(); }} fullWidth maxWidth="sm" fullScreen={isMobile} disableEscapeKeyDown={persisting || success}>
        <DialogTitle>Persist Scraper Results</DialogTitle>
        <DialogContent dividers>
          <Box mt={1}>
            <ProgressStream {...scrape.state} />
          </Box>
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <AlertTitle>Success!</AlertTitle>
              Scrape completed and data persisted.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={persisting}>
            {success ? `Close (${countdown})` : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* When a competitor is created, select it immediately and refresh the list */}
      <CompetitorModal
        open={openCompWizard}
        onClose={() => setOpenCompWizard(false)}
        onCompetitorAdded={async (newComp) => {
          try {
            // Refresh competitors so it appears in the dropdown
            const { data } = await axios.get(`${API}/api/scrapers/competitors`);
            const opts = [
              ...data.map(c => ({ id: c.id, label: c.name })),
              { id: 'add', label: 'Add competitor…' }
            ];
            setCompetitors(opts);
            // Select the new competitor
            setFormState(prev => ({ ...prev, competitor: { id: newComp.id, label: newComp.name } }));
            setIsDirty(true);
          } finally {
            setOpenCompWizard(false);
          }
        }}
      />
    </PageContainer>
  );
}
